import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// antd
import {
    Avatar,
    Row,
    message,
    Space,
    Typography,
    Modal,
    Image,
    Col,
    Tooltip,
} from 'antd'
import {
    DeleteOutlined,
    DownloadOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileWordOutlined,
} from '@ant-design/icons'

// slices
import { loginState } from '../../login/login.slice'
import {
    supportState,
    updateSupportComment,
    supportTaskById,
    selectTaskUpdate,
} from './support.slice'
import { projectsState } from '../../projects/projects.slice'

// components
import DeletePopUp from '../../../components/deletePopUp/deletePopUp'

import dayjs from 'dayjs'
import { SUPER_ADMIN, WORKSPACE_ADMIN } from '../../../constants/roles'
import { deletePermissionCheck } from '../../../helpers/permissionCheck'
import { teamListGet, teamState } from '../../team/team.slice'

function SupportCommentsSection({
    setFiles,
    files,
    mentionData,
    setFilteredMembers,
    setCommentChange,
    commentChange,
}) {
    const { Title, Text } = Typography

    const dispatch = useDispatch()

    const { userProfile } = useSelector(loginState)
    const { selectedProject, currentProjectData } = useSelector(projectsState)

    const { selectedTask } = useSelector(supportState)

    const [comment, setComment] = useState()
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteButtonLoading, setDeleteButtonLoading] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const [previewFileModal, setPreviewFileModal] = useState(false)
    const [imageData, setImageData] = useState('')

    const { pageLimit, teamList } = useSelector(teamState)

    const relativeTime = require('dayjs/plugin/relativeTime')
    dayjs.extend(relativeTime)

    useEffect(() => {
        if (selectedTask) {
            setCommentChange(selectedTask?.comments)
        }
    }, [selectedTask, setCommentChange])

    // Get Team List
    async function getTeamMember() {
        await dispatch(
            teamListGet({
                skip: 0,
                limit: pageLimit,
                projectId: userProfile?.activeProject?._id,
            })
        )
    }

    // DELETE COMMENT FOR SUPPORT
    async function deleteComment() {
        const result = await dispatch(
            updateSupportComment({
                id: selectedTask?._id,
                data: {
                    fieldName: 'remove',
                    commentId: comment?._id,
                },
            })
        )
        const data = result?.payload?.data
        if (data) {
            const { success, message: checkMessage } = data
            setComment()
            if (success) {
                message.success(checkMessage)
                setDeleteButtonLoading(false)
                setDeleteModal(false)
                const result = await dispatch(
                    supportTaskById({
                        _id: selectedTask?._id,
                    })
                )
                const data = result?.payload?.data
                if (data) {
                    if (data?.data?.[0]) {
                        dispatch(selectTaskUpdate(data?.data[0]))
                    } else if (data?.data) {
                        dispatch(selectTaskUpdate(data?.data))
                    }
                } else {
                    message.error('Something went wrong, try again later.')
                }
            } else {
                setDeleteModal(false)
                setDeleteButtonLoading(false)
                if (checkMessage) {
                    message.error(checkMessage)
                } else {
                    message.error('Something went wrong, try again later.')
                }
            }
        }
    }

    const fileTypes = [
        '.png',
        '.jpg',
        '.jpeg',
        '.gif',
        '.svg',
        '.mp4',
        '.mov',
        '.wmv',
        '.mkv',
    ]

    const videoTypes = ['.mp4', '.mov', '.wmv', '.mkv']

    const getFileIcon = (name) => {
        const ext = name.split('.').pop().toLowerCase()
        switch (ext) {
            case 'pdf':
                return (
                    <FilePdfOutlined
                        style={{ fontSize: 20, color: '#ff4d4f' }}
                    />
                )
            case 'doc':
            case 'docx':
                return (
                    <FileWordOutlined
                        style={{ fontSize: 20, color: '#1890ff' }}
                    />
                )
            case 'xls':
            case 'xlsx':
                return (
                    <FileExcelOutlined
                        style={{ fontSize: 20, color: '#52c41a' }}
                    />
                )

            default:
                return (
                    <FileOutlined style={{ fontSize: 20, color: '#595959' }} />
                )
        }
    }

    async function checkUser() {
        if (teamList?.length > 0) {
            let filterUsers = []

            teamList?.length > 0 &&
                teamList.forEach((member) => {
                    if (
                        member?._id !== userProfile?._id &&
                        !mentionData?.mentionDataList?.includes(member._id)
                    ) {
                        filterUsers = [
                            ...filterUsers,
                            {
                                id: member?._id,
                                display: `${member?.name}`,
                                profilePicture: member?.profilePicture,
                                email: member?.email,
                            },
                        ]
                    }
                })

            setFilteredMembers(filterUsers)
        } else {
            setFilteredMembers([])
        }
    }

    useEffect(() => {
        // TEAM MEMBER DATA SET WITH FILTER
        if (currentProjectData) {
            checkUser()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject, mentionData?.mentionDataList])

    useEffect(() => {
        getTeamMember()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
    const isImage = (url) => {
        const ext = url?.split('.').pop()?.toLowerCase()
        return imageExtensions.includes(ext)
    }
    return (
        <>
            <div style={{ backgroundColor: '#fff' }}>
                <DeletePopUp
                    previewDelete={deleteModal}
                    onDelete={() => {
                        setDeleteButtonLoading(true)
                        deleteComment()
                    }}
                    loadingButton={deleteButtonLoading}
                    onCancel={() => {
                        setDeleteButtonLoading(false)
                        setDeleteModal(false)
                    }}
                    modalTitle="Delete Comment"
                    pageKey="Comment"
                />
                <Modal
                    footer={null}
                    closable={false}
                    open={previewFileModal}
                    destroyOnClose={true}
                    centered
                    onCancel={() => setPreviewFileModal(false)}
                >
                    {videoTypes.some((r) => imageData.includes(r)) ? (
                        <video width="100%" controls>
                            <source src={imageData} />
                        </video>
                    ) : (
                        <Image
                            width="100%"
                            height="100%"
                            src={imageData}
                            preview={false}
                        />
                    )}
                </Modal>

                {commentChange?.length > 0 && (
                    <>
                        <Row
                            style={{
                                maxHeight: '55vh',
                                overflow: 'auto',
                            }}
                            justify={'space-between'}
                        >
                            {commentChange.map((data, idx) => (
                                <div key={idx} style={{ width: '100%' }}>
                                    <Row
                                        style={{
                                            minWidth: '100%',
                                        }}
                                        justify={'space-between'}
                                    >
                                        <Row
                                            style={{
                                                marginTop: 2,
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginRight: '10px',
                                                }}
                                            >
                                                {data?.user &&
                                                data?.user?.profilePicture ? (
                                                    <>
                                                        <Image
                                                            src={
                                                                data?.user
                                                                    ?.profilePicture
                                                            }
                                                            height={35}
                                                            width={35}
                                                            style={{
                                                                borderRadius:
                                                                    '50%',
                                                            }}
                                                            preview={false}
                                                        />
                                                    </>
                                                ) : (
                                                    <Avatar
                                                        style={{
                                                            backgroundColor:
                                                                'rgb(0 10 129 / 25%)',
                                                            cursor: 'pointer',
                                                            height: '35px',
                                                            width: '35px',
                                                        }}
                                                    >
                                                        {data?.username[0].toUpperCase()}
                                                        {data?.username.split(
                                                            ' '
                                                        )[1] !== undefined &&
                                                            data?.username
                                                                .split(
                                                                    ' '
                                                                )[1][0]
                                                                .toUpperCase()}
                                                    </Avatar>
                                                )}
                                            </div>
                                            <div>
                                                <div>
                                                    <Row align={'middle'}>
                                                        <Space>
                                                            <Title
                                                                level={5}
                                                                style={{
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {data?.username}
                                                            </Title>
                                                            <Text
                                                                style={{
                                                                    color: '#a9a4a4',
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {dayjs(
                                                                    data?.createdDate
                                                                ).fromNow()}
                                                            </Text>
                                                        </Space>
                                                    </Row>
                                                    <div
                                                        style={{
                                                            overflow: 'hidden',
                                                            display:
                                                                '-webkit-box',
                                                            WebkitBoxOrient:
                                                                'vertical',
                                                            WebkitLineClamp:
                                                                expanded
                                                                    ? 'none'
                                                                    : 3,
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: data?.content,
                                                        }}
                                                    />

                                                    {/* Show more / Show less */}
                                                    {data?.content &&
                                                        data.content.length >
                                                            100 && (
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() =>
                                                                    setExpanded(
                                                                        !expanded
                                                                    )
                                                                }
                                                            >
                                                                {expanded
                                                                    ? 'Show less'
                                                                    : 'Show more'}
                                                            </Text>
                                                        )}
                                                </div>
                                            </div>
                                        </Row>
                                        {(userProfile?.role?.roleName ===
                                            WORKSPACE_ADMIN ||
                                            userProfile?.role?.roleName ===
                                                SUPER_ADMIN ||
                                            data?.uId === userProfile?._id ||
                                            deletePermissionCheck(
                                                'tasks',
                                                userProfile
                                            )) && (
                                            <Row
                                                style={{
                                                    marginTop: 20,
                                                    textDecoration: 'underline',
                                                    color: '#ff4d4f',
                                                }}
                                                onClick={() => {
                                                    setDeleteModal(true)
                                                    setComment(data)
                                                }}
                                            >
                                                <Space>
                                                    <DeleteOutlined
                                                        style={{
                                                            fontSize: '18px',
                                                        }}
                                                    />
                                                </Space>
                                            </Row>
                                        )}
                                    </Row>
                                    <Row
                                        gutter={[12, 12]}
                                        style={{
                                            marginBottom: 20,
                                            marginLeft: '35px',
                                        }}
                                    >
                                        {Array.isArray(data?.attachments) &&
                                            data.attachments.length > 0 &&
                                            data.attachments.some(
                                                (item) => item
                                            ) &&
                                            data.attachments.map(
                                                (item, index) =>
                                                    item ? (
                                                        <Col
                                                            key={index}
                                                            xs={12}
                                                            sm={8}
                                                            md={6}
                                                            lg={6}
                                                        >
                                                            <div className="attachment-card">
                                                                {/* Thumbnail / Icon */}
                                                                {isImage(
                                                                    item
                                                                ) ? (
                                                                    <img
                                                                        src={
                                                                            item
                                                                        }
                                                                        alt="attachment"
                                                                        className="attachment-thumb"
                                                                    />
                                                                ) : (
                                                                    <div className="attachment-file-icon">
                                                                        {getFileIcon(
                                                                            item
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Filename */}
                                                                <Text
                                                                    ellipsis
                                                                    className="attachment-name"
                                                                >
                                                                    {item
                                                                        .split(
                                                                            '/'
                                                                        )
                                                                        .pop()}
                                                                </Text>

                                                                {/* Hover Overlay with Actions */}
                                                                <div className="attachment-overlay">
                                                                    <div className="overlay-actions">
                                                                        {/* View */}
                                                                        <Tooltip title="View">
                                                                            <EyeOutlined
                                                                                className="action-icon"
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    e.stopPropagation()
                                                                                    setImageData(
                                                                                        item
                                                                                    )
                                                                                    if (
                                                                                        fileTypes.some(
                                                                                            (
                                                                                                r
                                                                                            ) =>
                                                                                                item
                                                                                                    ?.toLowerCase()
                                                                                                    .includes(
                                                                                                        r
                                                                                                    )
                                                                                        )
                                                                                    ) {
                                                                                        setPreviewFileModal(
                                                                                            true
                                                                                        )
                                                                                    } else {
                                                                                        window.open(
                                                                                            item,
                                                                                            '_blank'
                                                                                        )
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Tooltip>

                                                                        {/* Download */}
                                                                        <Tooltip title="Download">
                                                                            <DownloadOutlined
                                                                                className="action-icon"
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    e.stopPropagation()
                                                                                    const link =
                                                                                        document.createElement(
                                                                                            'a'
                                                                                        )
                                                                                    link.href =
                                                                                        item
                                                                                    link.download =
                                                                                        item
                                                                                            .split(
                                                                                                '/'
                                                                                            )
                                                                                            .pop()
                                                                                    link.click()
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ) : null
                                            )}
                                    </Row>
                                </div>
                            ))}
                        </Row>
                    </>
                )}
            </div>
        </>
    )
}

export default SupportCommentsSection
